import React, { useState, useRef } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    FormControlLabel,
    Switch,
    Box,
    Alert,
    CircularProgress,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Brand } from "../../types";
import { productService } from "../../services/productService";

const schema = yup.object({
    name: yup.string().required("Brand name is required"),
    description: yup.string().optional(),
    display_order: yup.number().min(0, "Must be 0 or greater").optional(),
    is_active: yup.boolean().optional(),
    is_featured: yup.boolean().optional(),
});

interface CreateBrandFormData {
    name: string;
    description?: string;
    display_order?: number;
    is_active: boolean;
    is_featured: boolean;
}

interface CreateBrandModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess: (brand: Brand) => void;
}

export const CreateBrandModal: React.FC<CreateBrandModalProps> = ({
    open,
    onClose,
    onSuccess,
}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>("");
    const nameInputRef = useRef<HTMLInputElement | null>(null);

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<CreateBrandFormData>({
        resolver: yupResolver(schema) as any,
        defaultValues: {
            name: "",
            description: "",
            display_order: 0,
            is_active: true,
            is_featured: false,
        },
    });

    const handleFormSubmit = async (data: CreateBrandFormData) => {
        try {
            setLoading(true);
            setError("");

            const brandData = {
                name: data.name,
                description: data.description || undefined,
                display_order: data.display_order ?? 0,
                is_active: data.is_active,
                is_featured: data.is_featured ?? false,
            };

            const newBrand = await productService.createBrand(brandData);
            onSuccess(newBrand);
            handleClose();
        } catch (err: any) {
            setError(err.response?.data?.detail || "Failed to create brand");
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        reset();
        setError("");
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            disableRestoreFocus
            TransitionProps={{ onEntered: () => nameInputRef.current?.focus() }}
        >
            <DialogTitle>Create New Brand</DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Box component="form" sx={{ mt: 1 }}>
                    <Controller
                        name="name"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                margin="normal"
                                required
                                fullWidth
                                label="Brand Name"
                                inputRef={nameInputRef}
                                autoFocus
                                error={!!errors.name}
                                helperText={errors.name?.message}
                            />
                        )}
                    />

                    <Controller
                        name="description"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                margin="normal"
                                fullWidth
                                label="Description"
                                multiline
                                rows={3}
                                error={!!errors.description}
                                helperText={errors.description?.message}
                            />
                        )}
                    />

                    <Controller
                        name="display_order"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                margin="normal"
                                fullWidth
                                label="Display Order"
                                type="number"
                                inputProps={{ min: 0 }}
                                helperText="Lower numbers appear first"
                            />
                        )}
                    />

                    <Box sx={{ display: "flex", gap: 3, mt: 2 }}>
                        <Controller
                            name="is_active"
                            control={control}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            {...field}
                                            checked={field.value}
                                        />
                                    }
                                    label="Active"
                                />
                            )}
                        />
                        <Controller
                            name="is_featured"
                            control={control}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            {...field}
                                            checked={field.value}
                                            color="warning"
                                        />
                                    }
                                    label="Featured"
                                />
                            )}
                        />
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit(handleFormSubmit)}
                    variant="contained"
                    disabled={loading}
                >
                    {loading ? <CircularProgress size={24} /> : "Create Brand"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
